import { BrowserWindow, BrowserWindowSymbol, EditorFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { ComponentBoundingBox } from 'thunderbolt-viewer-manager-types'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import type { ViewerManagerLayoutApiEditorConfig, ICompBoundingBoxApi } from '../types'
import { getNotRotatedAbsoluteTop, getNotRotatedAbsoluteLeft } from '../utils'
import { name } from '../symbols'
import { getFullId } from '@wix/thunderbolt-commons'
import { InnerScrollApiSymbol } from '@wix/thunderbolt-becky-types'
import type { InnerScrollApiType, IMeasuresApi } from '@wix/thunderbolt-becky-types'
import { IRootScrollApi, RootScrollApiSymbol } from 'ds-feature-viewer-manager-scroll-api'
import { MeasuresApiSymbol } from 'ds-feature-measures-api'

type TotalScroll = {
	x: number
	y: number
}

type NodeData = { node: HTMLElement; boundingClientRect: DOMRect }

const getNodeData = (node: HTMLElement): NodeData => ({
	node,
	boundingClientRect: node.getBoundingClientRect(),
})

const getCompBoundingBox = ({ height, width, top, left }: DOMRect): ComponentBoundingBox => ({
	height,
	width,
	absoluteTop: top,
	absoluteLeft: left,
})

const isNodeVisible = (
	{ node, boundingClientRect }: NodeData,
	windowObject: NonNullable<BrowserWindow>,
	rootNode: HTMLElement
) => {
	if (node === rootNode) {
		return true
	}

	if (node.offsetHeight === 0 || node.offsetWidth === 0) {
		return false
	}

	const pointElement = windowObject.document.elementFromPoint(
		boundingClientRect.left + node.offsetWidth / 2,
		boundingClientRect.top + node.offsetHeight / 2
	)
	return !!pointElement?.closest(`#${rootNode.id}`)
}

const componentBoundingBoxApiFactory = (
	config: ViewerManagerLayoutApiEditorConfig,
	window: NonNullable<BrowserWindow>,
	innerScrollApi: InnerScrollApiType,
	rootScrollApi: IRootScrollApi,
	measuresApi: IMeasuresApi
): ICompBoundingBoxApi => {
	const getAncestorsInnerScroll = (compId: string): TotalScroll | undefined => {
		const realCompId = getFullId(compId)
		const ancestorsFromStructure = config.compIdToAncestors[realCompId]?.ancestorsFromRootStructure || []
		let innerScroll

		for (const ancestor of ancestorsFromStructure) {
			innerScroll = innerScrollApi.getComponentInnerScroll(ancestor)

			if (innerScroll) {
				break
			}
		}

		return innerScroll
	}

	const getCompTotalScroll = (compId: string): TotalScroll => {
		const innerScroll = getAncestorsInnerScroll(compId)
		return innerScroll
			? innerScroll
			: {
					y: 0,
					x: 0,
			  }
	}

	const getCompMeasuredBoundingBox = (
		compId: string,
		{ height, width, top, left }: DOMRect
	): ComponentBoundingBox => {
		const totalScroll = getCompTotalScroll(compId)
		const rootScroll = rootScrollApi.getRootScrollForComp(compId)

		return {
			height,
			width,
			absoluteTop: rootScroll.y + totalScroll.y + top,
			absoluteLeft: rootScroll.x + totalScroll.x + left,
		}
	}

	const getRotationInRadians = (node: HTMLElement) => {
		const transform = window.getComputedStyle(node).transform

		return transform === 'none' ? null : Math.asin(Number(transform.split('(')[1].split(')')[0].split(',')[1]))
	}

	const toNonRotated = (
		compId: string,
		node: HTMLElement,
		boundingBox: ComponentBoundingBox
	): ComponentBoundingBox => {
		const rotationInRad = getRotationInRadians(node)

		if (!rotationInRad) {
			return boundingBox
		}

		const width = node.offsetWidth
		const height = node.offsetHeight
		const absoluteTop = getNotRotatedAbsoluteTop(boundingBox.absoluteTop, boundingBox.height, height)
		const absoluteLeft = getNotRotatedAbsoluteLeft(boundingBox.absoluteLeft, boundingBox.width, width)

		return {
			height,
			width,
			absoluteTop,
			absoluteLeft,
		}
	}

	const getNodeFinalBoundingBox = (
		windowObject: NonNullable<BrowserWindow>,
		compId: string,
		{ node, boundingClientRect }: NodeData,
		withRotationFix = true
	) => {
		const compBoundingBox = getCompMeasuredBoundingBox(compId, boundingClientRect)

		if (withRotationFix) {
			return toNonRotated(compId, node, compBoundingBox)
		}

		return compBoundingBox
	}

	const getNodeFinalRelativeToViewportBoundingBox = (
		compId: string,
		{ node, boundingClientRect }: NodeData
	): ComponentBoundingBox => toNonRotated(compId, node, getCompBoundingBox(boundingClientRect))

	return {
		getComponentBoundingBox: (compId): ComponentBoundingBox => {
			const node = measuresApi.getCompElement(compId)
			return node ? getNodeFinalBoundingBox(window, compId, getNodeData(node)) : ({} as ComponentBoundingBox)
		},
		getComponentInnerElementBoundingBoxes: (compId, selector): Array<ComponentBoundingBox> => {
			const rootNode = measuresApi.getCompElement(compId)
			if (!rootNode) {
				return [] as Array<ComponentBoundingBox>
			}

			const nodes = selector ? Array.from(rootNode.querySelectorAll<HTMLElement>(selector)) : [rootNode]
			const nodesData = nodes.map((node) => getNodeData(node))
			const visibleNodesData = nodesData.filter((nodeData) => isNodeVisible(nodeData, window, rootNode))
			return visibleNodesData.map((nodeData) => getNodeFinalBoundingBox(window, compId, nodeData))
		},

		getRelativeToViewportBoundingBox: (compId): ComponentBoundingBox => {
			const node = measuresApi.getCompElement(compId)

			return node
				? getNodeFinalRelativeToViewportBoundingBox(compId, getNodeData(node))
				: ({} as ComponentBoundingBox)
		},

		getOriginalComponentBoundingBox: (compId): ComponentBoundingBox => {
			const node = measuresApi.getCompElement(compId)
			return node
				? getNodeFinalBoundingBox(window, compId, getNodeData(node), false)
				: ({} as ComponentBoundingBox)
		},
	}
}

export const ComponentBoundingBoxApi = withDependencies(
	[
		named(EditorFeatureConfigSymbol, name),
		BrowserWindowSymbol,
		InnerScrollApiSymbol,
		RootScrollApiSymbol,
		MeasuresApiSymbol,
	],
	componentBoundingBoxApiFactory
)
